import { useEffect } from "react";

export const SITE_URL = "https://arjunnasoni.in";
const DEFAULT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8f2b01fa-45fc-4563-a263-420554666e1a/images/75bc4c02c8fdc45e468509d9e8538e4b030e3b07129305ba582aa5dd9dcea0ba.jpeg";

function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-page SEO: title, description, canonical, OG/Twitter, robots, JSON-LD.
 * suffix defaults to the brand; articles can pass a shorter one.
 */
export function useSeo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  suffix = "Anvay — Arjunna Soni",
  jsonLd = null,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${suffix}` : `Family Constellation Therapy in Ludhiana & Online | ${suffix}`;
    document.title = fullTitle;

    const url = SITE_URL + (path || window.location.pathname);
    const desc = description || "Family & Systemic Constellation therapy with Arjunna Soni in Ludhiana and online. Grounded, confidential, non-mystical.";

    setMeta("name", "description", desc);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", url);

    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", image);

    // JSON-LD (per page)
    const existing = document.getElementById("page-jsonld");
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, image, type, noindex, suffix, JSON.stringify(jsonLd)]);
}

// ---- JSON-LD builders ----
export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q || f.question,
    acceptedAnswer: { "@type": "Answer", text: f.a || f.answer },
  })),
});

export const articleJsonLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  author: { "@type": "Person", name: post.author || "Arjunna Soni" },
  datePublished: post.publishedAt,
  publisher: { "@type": "Organization", name: "Anvay — Arjunna Soni" },
  mainEntityOfPage: SITE_URL + "/resources/" + post.slug,
});

export const serviceJsonLd = (s) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: s.title,
  description: s.shortDescription,
  serviceType: "Family Constellation Therapy",
  provider: { "@type": "ProfessionalService", name: "Anvay — Arjunna Soni", areaServed: "Ludhiana, India" },
  offers: { "@type": "Offer", price: s.price, priceCurrency: s.currency || "INR" },
});

export const personJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Arjunna Soni",
  jobTitle: "Family & Systemic Constellation Facilitator",
  worksFor: { "@type": "ProfessionalService", name: "Anvay" },
  url: SITE_URL + "/about",
  sameAs: ["https://instagram.com/arjunnasoni"],
});
